import type { ShopSummary, ShopType, UpdateShopRequest } from '../../api/types.ts';
import { initialDeviceCountForm, validateDeviceCountForm, type DeviceCountFormState } from './deviceCountHelpers.ts';

export interface ShopEditFormState extends DeviceCountFormState {
  name: string;
  shop_type: ShopType;
  currency_code: string;
  country_code: string;
  email: string;
  phone: string;
  address: string;
}

// Each mirrors its own server rule (routes/shops.ts): currency is 3-8 letters,
// country is ISO-3 exactly. Blank is allowed and simply omits the field.
const CURRENCY_CODE_PATTERN = /^[A-Z]{3,8}$/;
const COUNTRY_CODE_PATTERN = /^[A-Z]{3}$/;

export function initialShopEditForm(shop: ShopSummary): ShopEditFormState {
  return {
    name: shop.name,
    shop_type: shop.shop_type,
    currency_code: shop.currency_code ?? '',
    country_code: shop.country_code ?? '',
    email: shop.email ?? '',
    phone: shop.phone ?? '',
    address: shop.address ?? '',
    ...initialDeviceCountForm(shop),
  };
}

/** Empty is always allowed -- a blank field is omitted from the payload, and a
 *  shop has no currency at all until its first sync. */
function codeProblem(value: string, pattern: RegExp, message: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return pattern.test(trimmed) ? null : message;
}

export function validateCurrencyCode(value: string): string | null {
  return codeProblem(value, CURRENCY_CODE_PATTERN, 'Currency code must be 3-8 uppercase letters.');
}

export function validateCountryCode(value: string): string | null {
  return codeProblem(value, COUNTRY_CODE_PATTERN, 'Country code must be 3 uppercase letters.');
}

/** Returns the first validation problem, or null when the form is submittable. */
export function validateShopEditForm(form: ShopEditFormState): string | null {
  if (form.name.trim() === '') return 'Enter a shop name.';
  const currency = validateCurrencyCode(form.currency_code);
  if (currency) return currency;
  const country = validateCountryCode(form.country_code);
  if (country) return country;
  return validateDeviceCountForm(form);
}

/** A changed code: the new value, or undefined to OMIT it. The two codes have
 *  no clearing path -- the server takes absent as unchanged so sync stays the
 *  authority, and rejects an explicit null -- so a blanked field is omitted. */
function codePatch(current: string, original: string | null): string | undefined {
  const trimmed = current.trim();
  if (trimmed === '' || trimmed === (original ?? '')) return undefined;
  return trimmed;
}

/** A changed optional text field: undefined (omit -- unchanged), null
 *  (cleared), or the new trimmed value. */
function contactPatch(current: string, original: string | null): string | null | undefined {
  const trimmed = current.trim();
  const originalValue = original ?? '';
  if (trimmed === originalValue) return undefined;
  return trimmed === '' ? null : trimmed;
}

export function buildShopUpdatePayload(form: ShopEditFormState, shop: ShopSummary): UpdateShopRequest {
  const payload: UpdateShopRequest = {};

  const name = form.name.trim();
  if (name !== shop.name) payload.name = name;
  if (form.shop_type !== shop.shop_type) payload.shop_type = form.shop_type;

  const currency = codePatch(form.currency_code, shop.currency_code);
  if (currency !== undefined) payload.currency_code = currency;
  const country = codePatch(form.country_code, shop.country_code);
  if (country !== undefined) payload.country_code = country;

  const email = contactPatch(form.email, shop.email);
  if (email !== undefined) payload.email = email;
  const phone = contactPatch(form.phone, shop.phone);
  if (phone !== undefined) payload.phone = phone;
  const address = contactPatch(form.address, shop.address);
  if (address !== undefined) payload.address = address;

  const deviceAdmin = Number(form.device_count_admin);
  if (deviceAdmin !== shop.device_count_admin) payload.device_count_admin = deviceAdmin;
  const deviceTeller = Number(form.device_count_teller);
  if (deviceTeller !== shop.device_count_teller) payload.device_count_teller = deviceTeller;
  const devicePayout = Number(form.device_count_payout);
  if (devicePayout !== shop.device_count_payout) payload.device_count_payout = devicePayout;

  return payload;
}
